import { useEffect, useMemo, useRef, useState } from 'react'

import type { SuggestedPropertyRatePackageResult } from 'hooks/OfferPage/useSuggestedPropertyRatePackageOptionFromApi'

import PropertyRateOption from './PropertyRateOption'
import PropertyRefundabilitySelector from './PropertyRefundabilitySelector'

interface Props {
  lpcOffer?: App.HotelOffer
  rates: Array<App.EnrichedPropertyRate>
  roomListId: string
  roomPosition: number
  rateListId: string
  propertyTimezone: string
  suggestedPackageOption?: SuggestedPropertyRatePackageResult
  selectedSuggestedRateId?: string
  suggestedPackageSelectionRequestId?: number
}

/**
 * Merged variant that combines property rates with radio button selection.
 *
 * New UI structure: Radio selector for rates (by rate ID) injected into PropertyRateOption where the cancellation policy would normally go
 */
function PropertyRateOptionMerged(props: Props) {
  const {
    lpcOffer,
    rates,
    roomListId,
    roomPosition,
    rateListId,
    propertyTimezone,
    suggestedPackageOption,
    selectedSuggestedRateId,
    suggestedPackageSelectionRequestId,
  } = props

  // Default to first rate in the group
  const defaultRateId = rates[0]?.rateId
  const [selectedRateId, setSelectedRateId] = useState<string | undefined>(defaultRateId)
  const lastAppliedSuggestedPackageSelectionRequestId = useRef<number | undefined>(undefined)

  const activeRate = useMemo(() => {
    return rates.find(rate => rate.rateId === selectedRateId) || rates[0]
  }, [rates, selectedRateId])

  useEffect(() => {
    if (!suggestedPackageSelectionRequestId) {
      lastAppliedSuggestedPackageSelectionRequestId.current = undefined
      return
    }

    if (!selectedSuggestedRateId || lastAppliedSuggestedPackageSelectionRequestId.current === suggestedPackageSelectionRequestId) return

    const suggestedRate = rates.find(rate => rate.service.type === 'lpc' && rate.rateId === selectedSuggestedRateId)
    if (!suggestedRate) return

    lastAppliedSuggestedPackageSelectionRequestId.current = suggestedPackageSelectionRequestId
    setSelectedRateId(currentRateId => currentRateId === suggestedRate.rateId ? currentRateId : suggestedRate.rateId)
  }, [rates, selectedSuggestedRateId, suggestedPackageSelectionRequestId])

  const hasChoice = rates.length > 1

  if (!activeRate) {
    return null
  }

  const suggestedPackageMessage = !!suggestedPackageOption && activeRate.service.type === 'lpc' && suggestedPackageOption.rateId === activeRate.rateId ?
    suggestedPackageOption.message :
    undefined

  const cancellationPolicyContentOverride =
    hasChoice ?
        <PropertyRefundabilitySelector
          rates={rates}
          selectedRateId={selectedRateId}
          onChange={setSelectedRateId}
          propertyTimezone={propertyTimezone}
        /> :
      undefined

  return <PropertyRateOption
    lpcOffer={lpcOffer}
    rate={activeRate}
    roomListId={roomListId}
    roomPosition={roomPosition}
    rateListId={rateListId}
    ratePosition={activeRate.defaultOrder + 1}
    cancellationPolicyContentOverride={cancellationPolicyContentOverride}
    suggestedPackageMessage={suggestedPackageMessage}
  />
}

export default PropertyRateOptionMerged
